import React, { useEffect, useState, useCallback } from 'react';

export default function AdminGeneralPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [deleteBefore, setDeleteBefore] = useState('');
  const [shopItems, setShopItems] = useState([]);

  const fetchShopItems = useCallback(async () => {
    try {
      const res = await fetch('/api/manage-paddles', { credentials: 'include' });
      if (res.ok) setShopItems(await res.json());
    } catch {
      // Shop items are only needed for the credit-eligibility picker
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch('/api/settings', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load settings');
      setSettings(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const res = await fetch('/api/manage-metrics?limit=200', { credentials: 'include' });
      if (res.ok) setEvents(await res.json());
    } catch {
      // ignore
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); fetchEvents(); fetchShopItems(); }, [fetchSettings, fetchEvents, fetchShopItems]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({ ...settings, [name]: type === 'checkbox' ? checked : value });
  };

  const eligibleIds = (settings && settings.creditEligibleItemIds) || [];

  const toggleCreditItem = (itemId) => {
    const next = eligibleIds.includes(itemId)
      ? eligibleIds.filter((id) => id !== itemId)
      : [...eligibleIds, itemId];
    setSettings({ ...settings, creditEligibleItemIds: next });
  };

  const selectAllCreditItems = () => {
    setSettings({ ...settings, creditEligibleItemIds: shopItems.map((i) => i.id) });
  };

  const deselectAllCreditItems = () => {
    setSettings({ ...settings, creditEligibleItemIds: [] });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = 'Save failed';
        try { msg = JSON.parse(text).error || msg; } catch {}
        throw new Error(msg);
      }
      setSettings(await res.json());
      setMessage('Settings saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMetrics = async () => {
    if (!deleteBefore) {
      setError('Please choose a date first.');
      return;
    }
    if (!window.confirm(`Delete all login metrics before ${deleteBefore}?`)) return;
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/manage-metrics?before=${encodeURIComponent(deleteBefore)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      setMessage(data.message || 'Metrics deleted.');
      await fetchEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="page"><div className="loading">Loading...</div></div>;
  if (!settings) return <div className="page"><div className="alert alert-error">{error || 'Unable to load settings.'}</div></div>;

  return (
    <div className="page admin-page">
      <div className="admin-header">
        <h1>General Settings</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <form className="admin-form" onSubmit={handleSave}>
        <h2>Sessions</h2>
        <div className="form-group">
          <label htmlFor="userSessionDays">User session length (days)</label>
          <input id="userSessionDays" name="userSessionDays" type="number" min="0.01" step="0.01"
            value={settings.userSessionDays} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="adminSessionDays">Admin session length (days)</label>
          <input id="adminSessionDays" name="adminSessionDays" type="number" min="0.01" step="0.01"
            value={settings.adminSessionDays} onChange={handleChange} />
        </div>

        <h2>Login</h2>
        <div className="form-group form-group-checkbox">
          <label>
            <input type="checkbox" name="allowLoginCode" checked={!!settings.allowLoginCode} onChange={handleChange} />
            Allow 6-digit login codes
          </label>
        </div>

        <h2>Contact</h2>
        <div className="form-group">
          <label htmlFor="supportEmail">Support email (Contact Us)</label>
          <input id="supportEmail" name="supportEmail" type="email"
            value={settings.supportEmail} onChange={handleChange} placeholder="support@example.com" />
        </div>

        <h2>Tax</h2>
        <div className="form-group">
          <label htmlFor="taxRatePercent">Sales tax rate (%)</label>
          <input id="taxRatePercent" name="taxRatePercent" type="number" min="0" step="0.01"
            value={settings.taxRatePercent} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="taxState">Taxable state</label>
          <input id="taxState" name="taxState" value={settings.taxState} onChange={handleChange} placeholder="michigan" />
        </div>

        <h2>Metrics</h2>
        <div className="form-group">
          <label htmlFor="metricsRetentionDays">Keep login metrics for (days)</label>
          <input id="metricsRetentionDays" name="metricsRetentionDays" type="number" min="1" step="1"
            value={settings.metricsRetentionDays} onChange={handleChange} />
        </div>

        <h2>Credits</h2>
        <div className="form-group">
          <label htmlFor="creditPopupMessage">Message shown when a user applies a credit</label>
          <textarea id="creditPopupMessage" name="creditPopupMessage" rows={3}
            value={settings.creditPopupMessage || ''} onChange={handleChange}
            placeholder="A credit will be applied to this purchase..." />
        </div>
        <div className="form-group">
          <div className="credit-items-header">
            <label className="form-label">Credit-eligible shop items</label>
            <div className="credit-items-actions">
              <button type="button" className="btn btn-sm" onClick={selectAllCreditItems}>Select all</button>
              <button type="button" className="btn btn-sm" onClick={deselectAllCreditItems}>Deselect all</button>
            </div>
          </div>
          <div className="picker-table-wrap">
            {shopItems.length === 0 ? (
              <p className="picker-empty">No shop items found.</p>
            ) : (
              <table className="picker-table">
                <thead>
                  <tr>
                    <th className="picker-check" scope="col"><span className="sr-only">Eligible</span></th>
                    <th scope="col">Item</th>
                  </tr>
                </thead>
                <tbody>
                  {shopItems.map((item) => {
                    const checked = eligibleIds.includes(item.id);
                    return (
                      <tr
                        key={item.id}
                        className={checked ? 'row-selected' : undefined}
                        onClick={() => toggleCreditItem(item.id)}
                      >
                        <td className="picker-check">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCreditItem(item.id)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`${item.name} accepts credits`}
                          />
                        </td>
                        <td>{item.name}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <small className="visibility-note">
            {eligibleIds.length} of {shopItems.length} item{shopItems.length === 1 ? '' : 's'} accept credits.
          </small>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
        </div>
      </form>

      <div className="sections-manager">
        <h2>Login Metrics</h2>
        <div className="metrics-toolbar">
          <label htmlFor="deleteBefore">Delete metrics before:</label>
          <input id="deleteBefore" type="date" value={deleteBefore} onChange={(e) => setDeleteBefore(e.target.value)} />
          <button type="button" className="btn btn-sm btn-danger" onClick={handleDeleteMetrics}>Delete</button>
          <button type="button" className="btn btn-sm" onClick={fetchEvents}>Refresh</button>
        </div>
        {eventsLoading ? (
          <div className="loading">Loading events...</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr><th>Email</th><th>When</th></tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={2} className="empty-state">No login events recorded.</td></tr>
              ) : (
                events.map((ev) => (
                  <tr key={ev.id}>
                    <td>{ev.email}</td>
                    <td>{new Date(ev.timestamp).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
